import React, { useState, useEffect, useMemo } from "react";
import { useActiveAccount, useActiveWallet } from "thirdweb/react";
import { ThirdwebWalletConnect } from "../components/ThirdwebWalletConnect";
// ABI will be loaded dynamically
import { logger } from "../utils/logger";
import { useHttp } from "../hooks/useHttp";
import { useNavigate } from "react-router-dom";
import { ethers } from "ethers";
import { getEventsRecursive } from "../utils/ethEvents";
import { getMewToyImageUrl } from "../utils/mew";
import { MEW_RPC_URL } from "../config/chain";
interface NFT {
  tokenId: string;
  name: string;
  image: string;
  uid?: string;
  tokenType?: number;
  secretLink?: string;
}

const SOCIAL_LINKS = {
  TWITTER: "https://x.com/mew",
  TELEGRAM: "https://t.me/mewsworld",
};

const Mew = () => {
  const navigate = useNavigate();
  const wallet = useActiveWallet();

  const account = useActiveAccount();
  const address = account?.address;
  const { sendRequest } = useHttp();
  // Contract address and ABI
  const contractAddress = process.env.REACT_APP_MEW_CONTRACT_ADDRESS;

  const [isSigning, setIsSigning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nfts, setNfts] = useState<NFT[]>([]);
  const [revealedLinks, setRevealedLinks] = useState<Record<string, string>>(
    {}
  );

  const [showAllNFTs, setShowAllNFTs] = useState(false);

  useEffect(() => {
    console.log(`Mew rewards page loaded`, { hasAddress: !!address });

    if (address) {
      logger.contract(`Loading user Digital Collectibles for address`, {
        address: address,
      });
      loadUserNFTs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  const loadUserNFTs = async () => {
    if (!address) return;

    logger.contract(`Starting to load user Digital Collectibles`, {
      address: address,
      contractAddress,
    });
    setIsLoading(true);
    setError(null);

    try {
      // Load ABI dynamically from public directory
      const response = await fetch("/MewNftAbi.json");
      const MewNftAbi = await response.json();
      logger.contract(`ABI loaded successfully`);

      if (!contractAddress) {
        logger.contractError(
          "Contract address is not configured",
          new Error("Missing REACT_APP_MEW_CONTRACT_ADDRESS")
        );
        setError("Contract address is not configured.");
        return;
      }

      // Create a read-only provider for the active chain (fallback to Avalanche public RPC)
      const rpcUrl = MEW_RPC_URL;
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);

      const contract = new ethers.Contract(
        contractAddress,
        MewNftAbi,
        provider
      );

      // Use smart account address for reading balance (NFTs are minted to smart account)
      console.log("address", address);
      const eoaAccount =
        (wallet as any)?.getAdminAccount?.() ||
        (wallet as any)?.getAccount?.() ||
        account;
      const eoaAddress = eoaAccount?.address;
      console.log("eoaAddress", eoaAddress);
      // Get balance from smart contract - use eoaAddress since NFTs are minted to EOA
      const balanceAddress = eoaAddress || address;
      const balance = await contract.balanceOf(balanceAddress);
      const nftCount = Number(balance);
      logger.contract(`Digital Collectible balance retrieved`, {
        address: balanceAddress,
        balance: nftCount,
      });

      if (nftCount === 0) {
        logger.contract(`No Digital Collectibles found for address`, {
          address: balanceAddress,
        });
        setNfts([]);
        return;
      }

      // Read Transfer events using recursive paged query and resolve current ownership quickly
      // Use same address for event filtering as balance check (eoaAddress)
      const addressLower = balanceAddress.toLowerCase();
      const START_BLOCK_ENV = process.env.REACT_APP_MEW_CONTRACT_DEPLOY_BLOCK;
      if (!START_BLOCK_ENV) {
        throw new Error("REACT_APP_MEW_CONTRACT_DEPLOY_BLOCK is not configured");
      }
      const START_BLOCK = parseInt(START_BLOCK_ENV, 10);

      const logsProvider = provider;
      const ethContract = new ethers.Contract(
        contractAddress,
        MewNftAbi,
        logsProvider
      );
      const [toEvents, fromEvents] = await Promise.all([
        getEventsRecursive({
          contract: ethContract,
          eventName: "Transfer",
          args: [null, balanceAddress],
          fromBlock: START_BLOCK,
        }),
        getEventsRecursive({
          contract: ethContract,
          eventName: "Transfer",
          args: [balanceAddress, null],
          fromBlock: START_BLOCK,
        }),
      ]);

      const combined = [...toEvents, ...fromEvents];
      combined.sort((a, b) => {
        if (a.blockNumber !== b.blockNumber)
          return b.blockNumber - a.blockNumber;
        return (b.logIndex ?? 0) - (a.logIndex ?? 0);
      });

      const ownedTokenIds: string[] = [];
      const seenTokenIds = new Set<string>();
      for (const ev of combined) {
        const args: any = ev.args;
        if (!args) continue;
        const tokenIdStr = args.tokenId?.toString?.();
        if (!tokenIdStr || seenTokenIds.has(tokenIdStr)) continue;
        seenTokenIds.add(tokenIdStr);
        const to = String(args.to).toLowerCase();
        if (to === addressLower) {
          ownedTokenIds.push(tokenIdStr);
          if (ownedTokenIds.length >= nftCount) break;
        }
      }

      const currentlyOwnedTokenIds = ownedTokenIds;

      logger.contract(`Owned tokenIds resolved from events`, {
        resolved: currentlyOwnedTokenIds.length,
        expected: nftCount,
        tokenIds: currentlyOwnedTokenIds,
      });

      // Get token types for each token
      const userNFTs: NFT[] = [];
      for (const tokenIdStr of currentlyOwnedTokenIds) {
        try {
          const tokenType = await contract.tokenType(tokenIdStr);
          const tokenTypeNum = Number(tokenType);
          userNFTs.push({
            tokenId: tokenIdStr,
            name: `Catch Mew If You Can #${tokenIdStr.padStart(3, '0')}`,
            tokenType: tokenTypeNum,
            image: getMewToyImageUrl(tokenTypeNum),
          });
        } catch (err) {
          logger.contractError(
            `Failed to get tokenType for tokenId ${tokenIdStr}`,
            err as Error
          );
          // Fallback if tokenType fails
          userNFTs.push({
            tokenId: tokenIdStr,
            name: `Catch Mew If You Can #${tokenIdStr.padStart(3, '0')}`,
            image: getMewToyImageUrl(undefined),
          });
        }
      }

      logger.contract(`Digital Collectible loading completed`, {
        totalFound: userNFTs.length,
        expected: nftCount,
        nfts: userNFTs.map((nft) => ({
          tokenId: nft.tokenId,
          tokenType: nft.tokenType,
        })),
      });

      setNfts(userNFTs);
      console.log("userNFTs", userNFTs);
    } catch (err) {
      logger.contractError(
        "Failed to load user Digital Collectibles",
        err as Error,
        {
          address: address,
          contractAddress,
        }
      );
      setError("Failed to load Digital Collectibles");
    } finally {
      setIsLoading(false);
    }
  };

  if (!address && !isLoading) {
    return (
      <div className="flex justify-center items-center bg-gray-50 min-h-[calc(100dvh-149.5px)] p-6">
        <div className="mx-auto max-w-md text-center">
          <div className="p-8 bg-white rounded-lg shadow-md">
            <img
              src="/images/223-logo.png"
              alt="223 Logo"
              className="mx-auto mb-4 max-w-[100px]"
            />

            <h1 className="mb-4 text-2xl font-bold text-gray-900">
              Connect Your Wallet
            </h1>

            <p className="mb-8 text-gray-600">
              Connect your wallet to view your 223 Digital Collectibles and
              reveal additional rewards.
            </p>

            {error && (
              <div className="px-4 py-3 mb-4 text-red-700 bg-red-100 rounded border border-red-400">
                {error}
              </div>
            )}

            <div className="flex justify-center">
              <ThirdwebWalletConnect showDropdown={false} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-6 space-y-5">
        <div className="border border-black rounded-[14px] p-3.5 w-full">
          <div className="flex justify-between items-center">
            <h6 className="font-bold leading-none">My 223 Portal</h6>

            <div className="flex items-center gap-1.5">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.3333 9.33333C11.5101 9.33333 11.6797 9.2631 11.8047 9.13807C11.9298 9.01305 12 8.84348 12 8.66667C12 8.48986 11.9298 8.32029 11.8047 8.19526C11.6797 8.07024 11.5101 8 11.3333 8C11.1565 8 10.987 8.07024 10.8619 8.19526C10.7369 8.32029 10.6667 8.48986 10.6667 8.66667C10.6667 8.84348 10.7369 9.01305 10.8619 9.13807C10.987 9.2631 11.1565 9.33333 11.3333 9.33333ZM11.3333 12C11.5101 12 11.6797 11.9298 11.8047 11.8047C11.9298 11.6797 12 11.5101 12 11.3333C12 11.1565 11.9298 10.987 11.8047 10.8619C11.6797 10.7369 11.5101 10.6667 11.3333 10.6667C11.1565 10.6667 10.987 10.7369 10.8619 10.8619C10.7369 10.987 10.6667 11.1565 10.6667 11.3333C10.6667 11.5101 10.7369 11.6797 10.8619 11.8047C10.987 11.9298 11.1565 12 11.3333 12ZM8.66667 8.66667C8.66667 8.84348 8.59643 9.01305 8.4714 9.13807C8.34638 9.2631 8.17681 9.33333 8 9.33333C7.82319 9.33333 7.65362 9.2631 7.5286 9.13807C7.40357 9.01305 7.33333 8.84348 7.33333 8.66667C7.33333 8.48986 7.40357 8.32029 7.5286 8.19526C7.65362 8.07024 7.82319 8 8 8C8.17681 8 8.34638 8.07024 8.4714 8.19526C8.59643 8.32029 8.66667 8.48986 8.66667 8.66667ZM8.66667 11.3333C8.66667 11.5101 8.59643 11.6797 8.4714 11.8047C8.34638 11.9298 8.17681 12 8 12C7.82319 12 7.65362 11.9298 7.5286 11.8047C7.40357 11.6797 7.33333 11.5101 7.33333 11.3333C7.33333 11.1565 7.40357 10.987 7.5286 10.8619C7.65362 10.7369 7.82319 10.6667 8 10.6667C8.17681 10.6667 8.34638 10.7369 8.4714 10.8619C8.59643 10.987 8.66667 11.1565 8.66667 11.3333ZM4.66667 9.33333C4.84348 9.33333 5.01305 9.2631 5.13807 9.13807C5.2631 9.01305 5.33333 8.84348 5.33333 8.66667C5.33333 8.48986 5.2631 8.32029 5.13807 8.19526C5.01305 8.07024 4.84348 8 4.66667 8C4.48986 8 4.32029 8.07024 4.19526 8.19526C4.07024 8.32029 4 8.48986 4 8.66667C4 8.84348 4.07024 9.01305 4.19526 9.13807C4.32029 9.2631 4.48986 9.33333 4.66667 9.33333ZM4.66667 12C4.84348 12 5.01305 11.9298 5.13807 11.8047C5.2631 11.6797 5.33333 11.5101 5.33333 11.3333C5.33333 11.1565 5.2631 10.987 5.13807 10.8619C5.01305 10.7369 4.84348 10.6667 4.66667 10.6667C4.48986 10.6667 4.32029 10.7369 4.19526 10.8619C4.07024 10.987 4 11.1565 4 11.3333C4 11.5101 4.07024 11.6797 4.19526 11.8047C4.32029 11.9298 4.48986 12 4.66667 12Z"
                  fill="black"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M4.66665 1.16667C4.79926 1.16667 4.92644 1.21935 5.0202 1.31312C5.11397 1.40689 5.16665 1.53406 5.16665 1.66667V2.17534C5.60798 2.16667 6.09398 2.16667 6.62865 2.16667H9.37065C9.90598 2.16667 10.392 2.16667 10.8333 2.17534V1.66667C10.8333 1.53406 10.886 1.40689 10.9798 1.31312C11.0735 1.21935 11.2007 1.16667 11.3333 1.16667C11.4659 1.16667 11.5931 1.21935 11.6869 1.31312C11.7806 1.40689 11.8333 1.53406 11.8333 1.66667V2.21801C12.0067 2.23134 12.1709 2.24823 12.326 2.26867C13.1073 2.37401 13.74 2.59534 14.2393 3.094C14.738 3.59334 14.9593 4.22601 15.0647 5.00734C15.1667 5.76734 15.1667 6.73734 15.1667 7.96267V9.37067C15.1667 10.596 15.1667 11.5667 15.0647 12.326C14.9593 13.1073 14.738 13.74 14.2393 14.2393C13.74 14.738 13.1073 14.9593 12.326 15.0647C11.566 15.1667 10.596 15.1667 9.37065 15.1667H6.62998C5.40465 15.1667 4.43398 15.1667 3.67465 15.0647C2.89332 14.9593 2.26065 14.738 1.76132 14.2393C1.26265 13.74 1.04132 13.1073 0.935984 12.326C0.833984 11.566 0.833984 10.596 0.833984 9.37067V7.96267C0.833984 6.73734 0.833984 5.76667 0.935984 5.00734C1.04132 4.22601 1.26265 3.59334 1.76132 3.094C2.26065 2.59534 2.89332 2.37401 3.67465 2.26867C3.83021 2.24823 3.99443 2.23134 4.16732 2.21801V1.66667C4.16732 1.53418 4.2199 1.4071 4.31353 1.31335C4.40715 1.2196 4.53416 1.16685 4.66665 1.16667ZM3.80665 3.26C3.13665 3.35001 2.74998 3.51934 2.46798 3.80134C2.18598 4.08334 2.01665 4.47001 1.92665 5.14001C1.91154 5.25334 1.89865 5.37312 1.88798 5.49934H14.112C14.1013 5.37312 14.0884 5.25312 14.0733 5.13934C13.9833 4.46934 13.814 4.08267 13.532 3.80067C13.25 3.51867 12.8633 3.34934 12.1927 3.25934C11.508 3.16734 10.6047 3.16601 9.33332 3.16601H6.66665C5.39532 3.16601 4.49265 3.168 3.80665 3.26ZM1.83332 8.00001C1.83332 7.43067 1.83332 6.93534 1.84198 6.50001H14.158C14.1667 6.93534 14.1667 7.43067 14.1667 8.00001V9.33334C14.1667 10.6047 14.1653 11.508 14.0733 12.1933C13.9833 12.8633 13.814 13.25 13.532 13.532C13.25 13.814 12.8633 13.9833 12.1927 14.0733C11.508 14.1653 10.6047 14.1667 9.33332 14.1667H6.66665C5.39532 14.1667 4.49265 14.1653 3.80665 14.0733C3.13665 13.9833 2.74998 13.814 2.46798 13.532C2.18598 13.25 2.01665 12.8633 1.92665 12.1927C1.83465 11.508 1.83332 10.6047 1.83332 9.33334V8.00001Z"
                  fill="black"
                />
              </svg>

              <span className="text-xs">
                {new Date().toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="inline-block w-8 h-8 rounded-full border-b-2 border-gray-900 animate-spin"></div>
              <p className="ml-2 text-gray-600">
                Loading your Digital Collectibles...
              </p>
            </div>
          ) : nfts.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-gray-600">
                You don't own any 223 Digital Collectibles yet.
              </p>
            </div>
          ) : (
                <div className="mt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    {(showAllNFTs || nfts.length <= 4 ? nfts : nfts.slice(0, 4)).map((nft) => (
                      <div
                        key={nft.tokenId}
                        className="relative rounded-[14px] overflow-hidden aspect-square"
                      >
                        <img
                          src={nft.image}
                          alt={nft.name}
                          className="object-cover w-full h-full"
                        />
                        <div className="absolute inset-x-2.5 bottom-2.5 p-1 border border-white backdrop-blur-md rounded-lg">
                          <h3 className="text-sm font-bold text-white">
                            {nft.name}
                          </h3>
                        </div>
                      </div>
                    ))}
                  </div>

                  {nfts.length > 4 && (
                    <div className="flex justify-center">
                      <button
                        onClick={() => setShowAllNFTs(!showAllNFTs)}
                        className="px-6 py-2 text-sm font-semibold text-black bg-white border-2 border-black rounded-[50px] hover:bg-black hover:text-white transition-colors"
                      >
                        {showAllNFTs ? "Show Less" : `Show All (${nfts.length})`}
                      </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-gradient-to-r from-[#FD90E9] to-[#852A93] p-3.5 rounded-[10px] font-bold text-lg text-white text-center">
          Your unique NFT will keep updating. Protect your Character so you
          access all possible rewards. Forever.
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div
            className="rounded-[14px] overflow-hidden cursor-pointer"
            onClick={() => window.open(SOCIAL_LINKS.TELEGRAM, "_blank", "noopener,noreferrer")}
          >
            <img
              src="/images/mew/subscribe-email.png"
              alt="Subscribe to Email"
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="rounded-[14px] overflow-hidden cursor-pointer"
            onClick={() => window.open(SOCIAL_LINKS.TWITTER, "_blank", "noopener,noreferrer")}
          >
            <img
              src="/images/mew/twitter.png"
              alt="Follow on Twitter"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="relative rounded-[14px] overflow-hidden max-h-[266px]">
          <img
            src="/images/mew/play-mew-game.png"
            alt="Play the Mew Game"
            className="w-full h-full object-cover"
          />
          <button
            className="absolute bottom-[14px] left-[14px] right-[14px] h-[38px] border border-white rounded-[8px] backdrop-blur-md bg-white/20 font-bold text-[18px] text-white cursor-pointer"
          >
            Coming Soon
          </button>
        </div>

        <div className="relative rounded-[14px] overflow-hidden max-h-[266px] bg-gradient-to-r from-[#153967] to-[#3C72A1] p-3">
          <img
            src="/images/mew/choose-racker.png"
            alt="Complete your rewards"
            className="w-full h-full object-cover"
          />
          <button
            className="absolute bottom-[14px] left-[14px] right-[14px] h-[38px] border border-white rounded-[8px] backdrop-blur-md bg-white/20 font-bold text-[18px] text-white cursor-pointer"
          >
            Complete your rewards!
          </button>
          {/*
          <button
            onClick={() => window.open("#", "_blank", "noopener,noreferrer")}
            className="absolute bottom-[14px] left-[14px] right-[14px] h-[38px] border border-white rounded-[8px] backdrop-blur-md bg-white/20 font-bold text-[18px] text-white cursor-pointer"
          >
            Complete your rewards!
          </button>
          */}
        </div>
      </div>
    </>
  );
};

export default Mew;
